import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";

export interface DashboardMetrics {
    totalSubmitted: number;
    completedThisMonth: number;
    pipelineValue: number;
    bookedThisMonth: number;
}

export interface ChartData {
    name: string;
    value: number;
}

export interface DashboardAnalytics {
    metrics: DashboardMetrics;
    tendersByMonth: ChartData[];
    tendersByStatus: ChartData[];
    tendersByType: ChartData[];
}

export const getDashboardAnalytics = async (): Promise<{ success: boolean; data?: DashboardAnalytics; error?: string }> => {
    try {
        // Single auth call — get user and org together
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const { data: orgMember } = await supabase
            .from('base_organisation_members')
            .select('organisation_id')
            .eq('user_id', user.id)
            .single();

        if (!orgMember) throw new Error('User organization not found');

        // Fetch only the columns needed for analytics — no workflow_data, no heavy fields
        const { data: tenders, error } = await supabase
            .from('tender')
            .select('id, status, created_at')
            .eq('org_id', orgMember.organisation_id);

        if (error) throw error;

        const metrics: DashboardMetrics = {
            totalSubmitted: 0,
            completedThisMonth: 0,
            pipelineValue: 0,
            bookedThisMonth: 0,
        };

        const tendersByMonthMap: Record<string, number> = {};
        const tendersByStatusMap: Record<string, number> = {};

        for (const tender of tenders ?? []) {
            if (tender.status === 'submitted') metrics.totalSubmitted++;
            if (!['tender_ready', 'won', 'lost'].includes(tender.status)) metrics.pipelineValue++;
            if (['won', 'accepted'].includes(tender.status)) metrics.bookedThisMonth++;
            if (tender.status === 'tender_ready') metrics.completedThisMonth++;

            const monthKey = new Date(tender.created_at).toLocaleString('default', { month: 'short' });
            tendersByMonthMap[monthKey] = (tendersByMonthMap[monthKey] || 0) + 1;

            const statusLabel = (tender.status || 'unknown')
                .replace(/_/g, ' ')
                .replace(/\b\w/g, (l: string) => l.toUpperCase());
            tendersByStatusMap[statusLabel] = (tendersByStatusMap[statusLabel] || 0) + 1;
        }

        return {
            success: true,
            data: {
                metrics,
                tendersByMonth: Object.entries(tendersByMonthMap).map(([name, value]) => ({ name, value })),
                tendersByStatus: Object.entries(tendersByStatusMap).map(([name, value]) => ({ name, value })),
                tendersByType: [{ name: 'Standard', value: tenders?.length ?? 0 }],
            },
        };
    } catch (error: any) {
        console.error('[dashboard] Failed to load analytics:', error);
        return { success: false, error: error.message };
    }
};
